import { Alert, Box, Button, Chip, IconButton, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import UserFetch, { UserFetchJson, UserPostJson } from '../../userFetch';
import CreateImageDialog, { ImageCreateParams, LogLine } from './CreateImageDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import { onWsEvent, onWsConnectionChange } from '../../websocket';

interface ImageRecord {
  id: number;
  name: string;
  owner: string | null;
  platform: string;
  type: string;
  s3_key: string;
  size_bytes: number | null;
  checksum: string | null;
  uploaded_by: string | null;
  notes: string | null;
  created: string;
  url: string | null;
}

interface ImageProgress {
  step: string;
  percent: number;
  detail: string;
}

function ImagesPage() {
  const [images, setImages] = React.useState<ImageRecord[]>([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState<ImageRecord | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [builderConnected, setBuilderConnected] = React.useState(false);
  const [progress, setProgress] = React.useState<ImageProgress | null>(null);
  const [logLines, setLogLines] = React.useState<LogLine[]>([]);
  const [startTime, setStartTime] = React.useState<number | null>(null);
  const [buildDone, setBuildDone] = React.useState(false);
  const [wsConnected, setWsConnected] = React.useState(false);
  const [lastMessageAt, setLastMessageAt] = React.useState<number | null>(null);
  // Image count snapshot at build start — if a new image appears in the list after
  // this point while the build is running, we treat that as implicit completion.
  const buildStartImageCountRef = React.useRef<number>(0);

  const fetchImages = () => {
    UserFetchJson('/api/images')
      .then((data: ImageRecord[]) => setImages(data))
      .catch(() => {});
  };

  const fetchBuilderStatus = () => {
    UserFetchJson('/api/images/builder-status')
      .then((data: { connected: boolean }) => setBuilderConnected(data.connected))
      .catch(() => {});
  };

  React.useEffect(() => {
    fetchImages();
    fetchBuilderStatus();

    const unsub1 = onWsEvent('builder:status', (msg) => setBuilderConnected(msg.connected));
    const unsub2 = onWsEvent('image:progress', (msg) => {
      setProgress({ step: msg.step, percent: msg.percent, detail: msg.detail });
      setLogLines(lines => [...lines, { timestamp: Date.now(), step: msg.step, percent: msg.percent, detail: msg.detail }]);
      setLastMessageAt(Date.now());
    });
    // Rich stdio lines from builder subprocesses (gunzip/sfdisk/pishrink) — no percent,
    // just append to the log so users see what's happening during long opaque steps.
    const unsub2b = onWsEvent('image:log', (msg) => {
      setLogLines(lines => [...lines, { timestamp: Date.now(), step: msg.stage || 'log', percent: lines[lines.length - 1]?.percent ?? 0, detail: msg.detail }]);
      setLastMessageAt(Date.now());
    });
    const unsub3 = onWsEvent('image:complete', () => {
      setProgress({ step: 'done', percent: 100, detail: 'Image created successfully' });
      setLogLines(lines => [...lines, { timestamp: Date.now(), step: 'done', percent: 100, detail: 'Image created successfully' }]);
      setBuildDone(true);
      setLastMessageAt(Date.now());
      fetchImages();
      setSnackbar({ message: 'Image created successfully', severity: 'success' });
    });
    const unsub4 = onWsEvent('image:error', (msg) => {
      setLogLines(lines => [...lines, { timestamp: Date.now(), step: 'error', percent: lines[lines.length - 1]?.percent ?? 0, detail: `ERROR: ${msg.error}` }]);
      setBuildDone(true);
      setLastMessageAt(Date.now());
      setSnackbar({ message: `Image creation failed: ${msg.error}`, severity: 'error' });
    });
    const unsub5 = onWsConnectionChange(setWsConnected);

    return () => { unsub1(); unsub2(); unsub2b(); unsub3(); unsub4(); unsub5(); };
  }, []);

  const handleCreate = (params: ImageCreateParams) => {
    setLogLines([]);
    setBuildDone(false);
    setStartTime(Date.now());
    setLastMessageAt(Date.now());
    buildStartImageCountRef.current = images.length;
    setProgress({ step: 'starting', percent: 0, detail: 'Connecting to builder agent...' });
    UserPostJson('/api/images/create', params)
      .then((data) => {
        if (!data.message) {
          setStartTime(null);
          setProgress(null);
        }
      })
      .catch((err) => {
        setStartTime(null);
        setProgress(null);
        setSnackbar({ message: err.message || 'Failed to start image creation', severity: 'error' });
      });
  };

  // Recovery protections while a build is running:
  //  1. On WS reconnect, refetch immediately — server replays lastImageProgress, we
  //     also check if a new image appeared in the table (implicit completion).
  //  2. Poll every 15s regardless — belt-and-suspenders in case the WS replay missed
  //     anything or the server lost its in-progress state.
  const running = startTime !== null && !buildDone;
  React.useEffect(() => {
    if (!running) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      UserFetchJson('/api/images')
        .then((data: ImageRecord[]) => {
          if (cancelled) return;
          setImages(data);
          if (data.length > buildStartImageCountRef.current) {
            // A new image has appeared — the build finished while we weren't watching.
            setProgress({ step: 'done', percent: 100, detail: 'Image created successfully (recovered)' });
            setLogLines(lines => [...lines, { timestamp: Date.now(), step: 'recovered', percent: 100, detail: 'Image detected in catalog — build complete' }]);
            setBuildDone(true);
            setLastMessageAt(Date.now());
            setSnackbar({ message: 'Image created successfully', severity: 'success' });
          }
        })
        .catch(() => {});
    };
    const interval = setInterval(check, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [running]);

  // On WS reconnect while a build is running, re-fetch image list immediately.
  React.useEffect(() => {
    if (!running || !wsConnected) return;
    UserFetchJson('/api/images')
      .then((data: ImageRecord[]) => {
        setImages(data);
        if (data.length > buildStartImageCountRef.current) {
          setProgress({ step: 'done', percent: 100, detail: 'Image created successfully (recovered)' });
          setLogLines(lines => [...lines, { timestamp: Date.now(), step: 'recovered', percent: 100, detail: 'Image detected in catalog after reconnect — build complete' }]);
          setBuildDone(true);
          setLastMessageAt(Date.now());
          setSnackbar({ message: 'Image created successfully', severity: 'success' });
        }
      })
      .catch(() => {});
  }, [wsConnected, running]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    // Reset build state so the next opening starts clean
    setStartTime(null);
    setProgress(null);
    setBuildDone(false);
  };

  const handleClearLog = () => setLogLines([]);

  const handleDelete = (image: ImageRecord) => {
    UserFetch(`/api/images/${image.id}`, { method: 'DELETE' })
      .then((response) => {
        if (response.ok) {
          setDeleteConfirm(null);
          fetchImages();
          setSnackbar({ message: `Image "${image.name}" deleted`, severity: 'success' });
        }
      });
  };

  const handleShare = (image: ImageRecord) => {
    UserPostJson(`/api/images/${image.id}/share`, {})
      .then((data: { url: string; expires_at: string }) => {
        navigator.clipboard.writeText(data.url);
        setSnackbar({ message: 'Share link copied to clipboard (24h expiry)', severity: 'success' });
      })
      .catch(() => {
        setSnackbar({ message: 'Failed to generate share link', severity: 'error' });
      });
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Images</Typography>
          <Chip
            size="small"
            label={builderConnected ? 'Builder connected' : 'Builder offline'}
            color={builderConnected ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          size="small"
          disabled={!builderConnected}
        >
          Create Image
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {images.map((image) => (
              <TableRow key={image.id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{image.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={image.type === 'owner' ? 'Owner' : 'OS Update'}
                    color={image.type === 'owner' ? 'warning' : 'info'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{image.owner || '-'}</TableCell>
                <TableCell>{image.platform}</TableCell>
                <TableCell>{formatSize(image.size_bytes)}</TableCell>
                <TableCell>{formatDate(image.created)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {image.type === 'owner' && (
                    <IconButton size="small" onClick={() => handleShare(image)} title="Copy share link (24h)">
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => setDeleteConfirm(image)} title="Delete image">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {images.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No images yet. Connect the builder agent and create your first image.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateImageDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onCreate={handleCreate}
        progress={progress}
        logLines={logLines}
        startTime={startTime}
        buildDone={buildDone}
        onClearLog={handleClearLog}
        wsConnected={wsConnected}
        lastMessageAt={lastMessageAt}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Image"
        message={`Delete image "${deleteConfirm?.name}"? This will also remove it from S3.`}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ImagesPage;
